import { Link, Navigate, useParams } from 'react-router-dom';
import type { AdminAccountTransaction, GetAccountDetailResponse } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { usePricing } from '../api/pricing.ts';
import { useAccount } from '../api/useAdmin.ts';
import {
  formatLocalTimestamp,
  formatUsdMicrosOrCentsAsCnyDisplayBalance,
  formatUsdMicrosOrCentsAsCnyMetered,
  formatUsdMicrosOrCentsBalance,
  usdMicrosFromCentsFallback,
} from '../components/formatters.ts';
import { useT } from '../i18n/index.ts';
import {
  Button,
  Chip,
  KpiCard,
  PageHeader,
  Panel,
  Skeleton,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

/**
 * `/customers/accounts/:accountId` — resolving redirect to Customer 360.
 *
 * Retired as a full detail page in #861 PR B (epic #853 decision 3): the
 * Customer 360 page (`/crm/customers/:orgId`) is the single customer
 * detail surface. This route now:
 *
 *   1. Fetches the account (existing `useAccount` hook).
 *   2. Accounts WITH an org → `<Navigate replace>` to the 360 page. The
 *      `replace` semantics keep the browser back button working — going
 *      back skips this resolver instead of bouncing forward onto it
 *      again.
 *   3. Accounts WITHOUT an org (e.g. the seeded `acc_system`) have no 360
 *      view, so they keep a minimal read-only record here (summary +
 *      balance + recent transactions) instead of a dead end — epic #853
 *      acceptance criterion 3.
 *
 * Entry points that link by `account_id` (accounts list, command palette,
 * billing anomalies, payment orders, business dashboard, audit, gift
 * cards, request logs) intentionally keep their URLs and ride this
 * redirect.
 */

const MAX_RECENT_TRANSACTIONS = 20;

function formatIso(iso: string): string {
  return formatLocalTimestamp(iso, { seconds: true });
}

export function AccountDetail(): JSX.Element {
  const t = useT();
  const params = useParams<{ accountId: string }>();
  const accountId = params.accountId ?? '';
  const account = useAccount(accountId);

  if (account.isPending) {
    return (
      <section className="flex flex-col gap-6" data-testid="account-detail-loading">
        <p className="text-dense text-paper-low" role="status">
          {t('account_detail.resolving')}
        </p>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32" />
        <Skeleton className="h-48" />
        <span className="sr-only">{t('account_detail.loading_aria')}</span>
      </section>
    );
  }

  if (account.isError) {
    return (
      <section
        className="flex flex-col gap-4 rounded-lg border border-bad-line bg-bad-dim p-6"
        role="alert"
        data-testid="account-detail-error"
      >
        <h2 className="text-section text-bad">{t('account_detail.error.title')}</h2>
        <p className="text-body text-bad-bright">
          {account.error instanceof OpsApiError
            ? `${account.error.code}: ${account.error.message}`
            : t('common.network_error')}
        </p>
        <div className="flex items-center gap-3">
          <Button onClick={() => void account.refetch()}>{t('common.retry')}</Button>
          <Link
            to="/customers/accounts"
            className="text-dense text-paper-mid transition-colors duration-120 hover:text-signal"
          >
            {t('account_detail.back')}
          </Link>
        </div>
      </section>
    );
  }

  if (account.data.org !== null) {
    return (
      <Navigate
        to={`/crm/customers/${encodeURIComponent(account.data.org.org_id)}`}
        replace
      />
    );
  }

  return <OrglessAccountView account={account.data} />;
}

/**
 * Minimal read-only fallback for org-less accounts (no Customer 360 view
 * exists for them). Deliberately action-free: balance adjustment, email
 * verification, and lifecycle controls live on the 360 page, which is
 * org-keyed and therefore unreachable for these rows.
 */
function OrglessAccountView({
  account,
}: {
  readonly account: GetAccountDetailResponse;
}): JSX.Element {
  const t = useT();
  return (
    <section className="flex flex-col gap-6" data-testid="account-detail-page">
      <nav aria-label="breadcrumb" className="flex items-center gap-2 text-micro text-paper-low">
        <Link
          to="/customers/accounts"
          className="transition-colors duration-120 hover:text-signal"
        >
          {t('account_detail.breadcrumb.accounts')}
        </Link>
        <span aria-hidden>/</span>
        <span className="font-mono text-paper" data-testid="account-breadcrumb-email">
          {account.email}
        </span>
      </nav>

      <div className="flex flex-col gap-3">
        <PageHeader title={<span data-testid="account-detail-title">{account.email}</span>} />
        <div className="flex flex-wrap items-center gap-3 text-label text-paper-mid">
          {account.is_system ? (
            <Chip tone="neutral" testId="account-detail-system">
              {t('accounts.chip.system')}
            </Chip>
          ) : (
            <Chip tone="ok" testId="account-detail-system">
              {t('accounts.chip.tenant')}
            </Chip>
          )}
          <span aria-hidden>·</span>
          <span data-testid="account-detail-created">
            {t('account_detail.meta.created', { iso: formatIso(account.created_at) })}
          </span>
        </div>
      </div>

      <p
        role="status"
        className="rounded-lg border border-line-soft bg-well px-4 py-3 text-dense text-paper-mid"
        data-testid="account-detail-no-org-notice"
      >
        {t('account_detail.no_org.notice')}
      </p>

      <SummaryCard account={account} />
      <TransactionsCard transactions={account.recent_transactions} />
    </section>
  );
}

// ─── Summary card ────────────────────────────────────────────────────────

interface SummaryCardProps {
  readonly account: GetAccountDetailResponse;
}

function SummaryCard({ account }: SummaryCardProps): JSX.Element {
  const t = useT();
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;
  return (
    <Panel caption={t('account_detail.summary.title')} padded testId="account-summary-card">
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label={t('account_detail.summary.account_id')}
            value={account.account_id}
            mono
            testId="account-summary-id"
          />
          <Field
            label={t('account_detail.summary.org_id')}
            value={account.org?.org_id ?? '—'}
            mono
            testId="account-summary-org-id"
          />
          <Field
            label={t('account_detail.summary.org_name')}
            value={account.org?.name ?? '—'}
            testId="account-summary-org-name"
          />
          <Field
            label={t('account_detail.summary.member_role')}
            value={account.org?.member_role ?? '—'}
            mono
            testId="account-summary-member-role"
          />
        </div>
        <KpiCard
          label={t('account_detail.summary.current_balance')}
          value={
            <span data-testid="account-summary-balance">
              {formatUsdMicrosOrCentsAsCnyDisplayBalance(
                account.balance_usd_micros,
                account.balance_usd_cents,
                cnyPerUsd,
              )}
            </span>
          }
          delta={{
            text: (
              <>
                USD credit ledger:{' '}
                {formatUsdMicrosOrCentsBalance(account.balance_usd_micros, account.balance_usd_cents)}
              </>
            ),
            tone: 'neutral',
          }}
          testId="account-summary-balance-block"
        />
      </div>
    </Panel>
  );
}

interface FieldProps {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
  readonly testId?: string;
}

function Field({ label, value, mono, testId }: FieldProps): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5" data-testid={testId}>
      <span className="text-label text-paper-low">
        {label}
      </span>
      <span
        className={`break-all text-body text-paper ${mono === true ? 'font-mono' : ''}`}
      >
        {value}
      </span>
    </div>
  );
}

// ─── Transactions card ───────────────────────────────────────────────────

interface TransactionsCardProps {
  readonly transactions: readonly AdminAccountTransaction[];
}

function TransactionsCard({ transactions }: TransactionsCardProps): JSX.Element {
  const t = useT();
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;
  const rows = transactions.slice(0, MAX_RECENT_TRANSACTIONS);
  return (
    <div id="account-transactions">
      <Panel
        caption={t('account_detail.tx.title')}
        actions={
          <span className="text-micro text-paper-low">
            {t('account_detail.tx.last_n', { n: String(MAX_RECENT_TRANSACTIONS) })}
          </span>
        }
        padded
        testId="account-transactions-card"
      >
        {rows.length === 0 ? (
          <p className="text-dense text-paper-low" data-testid="account-transactions-empty">
            {t('account_detail.tx.empty')}
          </p>
        ) : (
          <Table testId="account-transactions-table">
            <THead>
              <Tr interactive={false}>
                <Th>{t('account_detail.tx.column.type')}</Th>
                <Th num>{t('account_detail.tx.column.amount')}</Th>
                <Th>{t('account_detail.tx.column.actor')}</Th>
                <Th>{t('account_detail.tx.column.reason')}</Th>
                <Th>{t('account_detail.tx.column.created')}</Th>
              </Tr>
            </THead>
            <TBody>
              {rows.map((tx) => {
                const amountUsdMicros = usdMicrosFromCentsFallback(
                  tx.amount_usd_micros,
                  tx.amount_usd_cents,
                );
                const amountClass =
                  amountUsdMicros > 0
                    ? 'text-ok'
                    : amountUsdMicros < 0
                      ? 'text-bad'
                      : 'text-paper-mid';
                return (
                  <Tr key={tx.id} data-testid={`account-transaction-row-${tx.id}`}>
                    <Td mono>{tx.type}</Td>
                    <Td
                      num
                      className={`font-semibold ${amountClass}`}
                      data-testid={`account-transaction-row-${tx.id}-amount`}
                    >
                      {formatUsdMicrosOrCentsAsCnyMetered(
                        tx.amount_usd_micros,
                        tx.amount_usd_cents,
                        cnyPerUsd,
                        true)}
                    </Td>
                    <Td>
                      <Chip tone="neutral" testId={`account-transaction-row-${tx.id}-actor`}>
                        {tx.actor_type}
                      </Chip>
                    </Td>
                    <Td emphasis>{tx.reason ?? '—'}</Td>
                    <Td mono>{formatIso(tx.created_at)}</Td>
                  </Tr>
                );
              })}
            </TBody>
          </Table>
        )}
      </Panel>
    </div>
  );
}
